import React, { useMemo, useState } from "react"
import { ChevronLeft, Shield, CheckCircle2, Check, Sparkles, RotateCw, ShieldCheck, Save, Pencil, Download, AlertTriangle } from "lucide-react"
import iarcSchema from "./data/iarcQuestionnaire.json"

// Same thresholds as getScoreColor in StoreValidator.jsx — kept local rather than imported to
// avoid coupling this component to that file's internals for one color scale.
const scoreColor = s => s >= 80 ? "#22c55e" : s >= 60 ? "#f59e0b" : s >= 40 ? "#f97316" : "#ef4444"

// Real IARC data (from a saved copy of the actual questionnaire): a single root question, every
// other question reachable only once a specific parent answer is selected (triggerResponseIDs).
// UI intentionally mirrors that source's own interaction model — answering a question reveals
// its follow-up(s) inline, indented right below it, rather than as separate pages/steps.
const buildChildrenByParent = schema => {
  const map = {}
  Object.entries(schema.questions).forEach(([id, q]) => {
    if (!q.parentQuestionID) return
    if (!map[q.parentQuestionID]) map[q.parentQuestionID] = []
    map[q.parentQuestionID].push(id)
  })
  return map
}

const isChildVisible = (child, parentAnswers) => child.triggerResponseIDs.some(rid => parentAnswers.includes(rid))

// Mirrors the real questionnaire's own behavior (QTCQuestion.hide() calls .clear() on every
// hidden follow-up) — changing an answer that hides a follow-up question also wipes out
// whatever was answered inside it (and its own follow-ups), so no invisible answer lingers.
const pruneInvisible = (questionId, answers, schema, childrenByParent) => {
  let next = answers
  const parentAnswers = answers[questionId] || []
  ;(childrenByParent[questionId] || []).forEach(childId => {
    const child = schema.questions[childId]
    if (!isChildVisible(child, parentAnswers)) {
      if (next[childId] !== undefined) { const { [childId]: _drop, ...rest } = next; next = rest }
      const stack = [...(childrenByParent[childId] || [])]
      while (stack.length) {
        const id = stack.pop()
        if (next[id] !== undefined) { const { [id]: _dropped, ...rest } = next; next = rest }
        stack.push(...(childrenByParent[id] || []))
      }
    } else {
      next = pruneInvisible(childId, next, schema, childrenByParent)
    }
  })
  return next
}

// Recursively count every currently-visible question, and how many of those have an answer —
// used for the progress summary. Only counts what's actually reachable right now, since the
// total varies a lot depending on which branches are open.
const countProgress = (questionId, schema, childrenByParent, answers) => {
  let total = 1
  let done = (answers[questionId] || []).length > 0 ? 1 : 0
  const selected = answers[questionId] || []
  ;(childrenByParent[questionId] || []).forEach(childId => {
    const child = schema.questions[childId]
    if (isChildVisible(child, selected)) {
      const sub = countProgress(childId, schema, childrenByParent, answers)
      total += sub.total
      done += sub.done
    }
  })
  return { total, done }
}

// Response shape for the real answers isn't confirmed yet (no sample response seen for
// /generate/iarc_questionnaire/ so far) — try the most likely candidate containers defensively,
// then only keep entries whose key is a real question in our schema (coercing each value to an
// array of response IDs) so an unexpected shape can't silently corrupt state. If this comes back
// empty, check the raw response logged by generateIarcQuestionnaire() in StoreValidator.jsx and
// share it so the mapping here can be corrected.
const extractIarcAnswers = (res, schema) => {
  // Confirmed key (2026-10, from the real /generate/complete/ and /validate/iarc_questionnaire/
  // responses): "iarc_questionnaire", sometimes nested under `listing` — checked first since
  // it's now evidenced, with the older unconfirmed guesses kept as a fallback.
  const candidate = res?.iarc_questionnaire || res?.listing?.iarc_questionnaire
    || res?.responses || res?.answers || res?.iarc_responses || res?.data?.responses || res
  if (!candidate || typeof candidate !== "object") return {}
  const recognized = {}
  Object.entries(candidate).forEach(([qid, val]) => {
    if (!schema.questions[qid]) return
    recognized[qid] = Array.isArray(val) ? val : [val]
  })
  return recognized
}

// Many real IARC option labels are "Short label - a longer explanation with examples." —
// splitting on the first " - " lets the short label read as a proper answer choice and the
// rest as supporting detail, instead of one long undifferentiated sentence.
const splitResponseText = text => {
  const idx = text.indexOf(" - ")
  if (idx === -1) return { label: text, detail: "" }
  return { label: text.slice(0, idx), detail: text.slice(idx + 3) }
}

// A real <input type=radio/checkbox> renders with no visible checked indicator anywhere else
// in this app's dark theme — every other selection UI here (see renderField's own "radio" and
// "checkboxes" branches) uses a plain button with a custom bordered indicator instead, so this
// matches that same established pattern rather than relying on native input styling.
const ResponseOption = ({ q, r, isSelected, onToggle, readOnly }) => {
  const { label, detail } = splitResponseText(r.text)
  const isCheckbox = q.inputType === "checkbox"
  return (
    <button
      type="button"
      disabled={readOnly}
      onClick={() => onToggle(r.id, !isSelected)}
      style={{
        display: "flex", gap: "9px", alignItems: "flex-start", cursor: readOnly ? "default" : "pointer", width: "100%", textAlign: "left",
        border: `1px solid ${isSelected ? "#F6255A55" : "#2E3244"}`,
        background: isSelected ? "#F6255A14" : "#171821",
        borderRadius: "8px", padding: "9px 11px", transition: "border-color 0.15s, background 0.15s",
      }}
      onMouseEnter={e => { if (!isSelected && !readOnly) e.currentTarget.style.borderColor = "#4b5563" }}
      onMouseLeave={e => { if (!isSelected && !readOnly) e.currentTarget.style.borderColor = "#2E3244" }}
    >
      <div style={{
        width: "15px", height: "15px", flexShrink: 0, marginTop: "2px",
        borderRadius: isCheckbox ? "3px" : "50%",
        border: `1.5px solid ${isSelected ? "#F6255A" : "#4b5563"}`,
        background: isSelected && isCheckbox ? "#F6255A" : "none",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {isSelected && (isCheckbox
          ? <Check size={10} color="#fff" strokeWidth={3} />
          : <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#F6255A" }} />)}
      </div>
      <span style={{ color: "#d1d5db", fontSize: "12.5px", lineHeight: 1.5 }}>
        <span style={{ fontWeight: isSelected ? 700 : 600 }}>{label}</span>
        {detail && <span style={{ color: "#6b7280" }}> — {detail}</span>}
      </span>
    </button>
  )
}

const QuestionBody = ({ questionId, schema, childrenByParent, answers, onAnswer, depth, readOnly }) => {
  const q = schema.questions[questionId]
  if (!q) return null
  const selected = answers[questionId] || []
  const isAnswered = selected.length > 0
  // The root's own direct children are the ~9 core topics, which already get their own
  // TopicCard treatment rendered separately below (see the comment on TopicCard) — recursing
  // into them here too would render every topic and its whole sub-tree a second time.
  const children = questionId === schema.root ? [] : (childrenByParent[questionId] || [])

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: "7px", marginBottom: "9px" }}>
        {isAnswered
          ? <CheckCircle2 size={14} color="#22c55e" style={{ flexShrink: 0, marginTop: "2px" }} />
          : <div style={{ width: "14px", height: "14px", borderRadius: "50%", border: "1.5px solid #4b5563", flexShrink: 0, marginTop: "2px" }} />}
        <p style={{ color: "#e5e7eb", fontSize: depth === 0 ? "14px" : "13px", fontWeight: depth === 0 ? 700 : 600, margin: 0, lineHeight: 1.5 }}>
          {q.text}
        </p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginLeft: "21px" }}>
        {q.responses.map(r => (
          <ResponseOption
            key={r.id} q={{ ...q, id: questionId }} r={r} isSelected={selected.includes(r.id)} readOnly={readOnly}
            onToggle={(responseId, checked) => onAnswer(questionId, responseId, checked, q.inputType)}
          />
        ))}
      </div>
      {children.map(childId => {
        const child = schema.questions[childId]
        return isChildVisible(child, selected) ? (
          <div key={childId} style={{ marginTop: "14px", marginLeft: "21px", paddingLeft: "16px", borderLeft: "2px solid #2E3244" }}>
            <QuestionBody questionId={childId} schema={schema} childrenByParent={childrenByParent} answers={answers} onAnswer={onAnswer} depth={depth + 1} readOnly={readOnly} />
          </div>
        ) : null
      })}
    </div>
  )
}

// Each direct child of the root (Violence, Sexuality, Gambling, Language, ...) is one of the
// ~9 core topics — giving each its own card makes a long flat list of categories easy to scan,
// instead of everything running together as one continuous block.
const TopicCard = ({ questionId, schema, childrenByParent, answers, onAnswer, readOnly }) => {
  const q = schema.questions[questionId]
  const progress = countProgress(questionId, schema, childrenByParent, answers)
  const isComplete = progress.done === progress.total
  return (
    <div style={{
      background: "#171821", border: `1px solid ${isComplete ? "#22c55e40" : "#2E3244"}`,
      borderRadius: "10px", padding: "16px 18px", marginBottom: "12px",
    }}>
      {q.category && (
        <span style={{ display: "block", color: "#F6255A", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 8px" }}>
          {q.category}
        </span>
      )}
      <QuestionBody questionId={questionId} schema={schema} childrenByParent={childrenByParent} answers={answers} onAnswer={onAnswer} depth={1} readOnly={readOnly} />
    </div>
  )
}

// Shared by both the full-screen questionnaire and the inline editor below — applies one answer
// change and re-prunes from that question down, so hiding a follow-up branch also clears
// whatever was answered inside it.
const makeAnswerHandler = (answers, childrenByParent, onChange) => (questionId, responseId, checked, inputType) => {
  const current = answers[questionId] || []
  const selected = inputType === "checkbox"
    ? (checked ? [...current, responseId] : current.filter(id => id !== responseId))
    : [responseId]
  const nextAnswers = { ...answers, [questionId]: selected }
  onChange(pruneInvisible(questionId, nextAnswers, iarcSchema, childrenByParent))
}

// Same red/orange/green severity language used for "low"/"medium"/"high" flagged categories.
const severityColor = s => {
  const v = (s || "").toLowerCase()
  if (v === "high") return "#ef4444"
  if (v === "medium") return "#f59e0b"
  if (v === "low") return "#22c55e"
  return "#6b7280"
}

// audience_fit and content_audience_fit (2026-10 feature) are two INDEPENDENT checks — one
// compares the user's own stated target audience against the predicted rating, the other
// compares the AI's own inferred audience (from the generated title/description/genre/theme)
// against the same predicted rating. They can disagree with each other, so each gets its own
// banner — never merged into one.
const IarcAudienceFitBanner = ({ label, fit }) => (
  <div style={{
    background: fit.fits ? "#22c55e0f" : "#F6255A0f",
    border: `1px solid ${fit.fits ? "#22c55e30" : "#F6255A40"}`,
    borderRadius: "10px", padding: "12px 14px", marginBottom: "12px",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: "7px", marginBottom: fit.recommendation ? "4px" : 0, flexWrap: "wrap" }}>
      {fit.fits ? <CheckCircle2 size={13} color="#22c55e" /> : <AlertTriangle size={13} color="#F6255A" />}
      <span style={{ color: "#fff", fontSize: "12px", fontWeight: 700 }}>{label}</span>
      {fit.target_audience && <span style={{ color: "#6b7280", fontSize: "11px" }}>— {fit.target_audience}</span>}
    </div>
    {fit.recommendation && <p style={{ color: "#9ca3af", fontSize: "11.5px", lineHeight: 1.6, margin: 0 }}>{fit.recommendation}</p>}
  </div>
)

// predicted_rating / audience_fit / content_audience_fit (2026-10 feature) — an ESTIMATE only,
// never a real IARC/ESRB/PEGI/etc determination (see the disclaimer rendered verbatim at the
// bottom, straight from the backend, rather than our own paraphrase of it). Shared by both the
// inline Content Rating editor and the full-screen questionnaire so the two never drift apart.
const IarcInsightsPanel = ({ insights, onDownloadCertificate, downloadingCertificate, certificateError }) => {
  if (!insights) return null
  const { predictedRating, audienceFit, contentAudienceFit } = insights
  const flaggedCategories = Object.entries(predictedRating?.flagged_categories || {})
  const authorities = Object.entries(predictedRating?.authorities || {})

  return (
    <div style={{ marginBottom: "4px" }}>
      {predictedRating && (
        <div style={{ background: "#171821", border: "1px solid #2E3244", borderRadius: "10px", padding: "16px 18px", marginBottom: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", marginBottom: "10px", flexWrap: "wrap" }}>
            <span style={{ color: "#fff", fontSize: "13px", fontWeight: 700 }}>Predicted content rating</span>
            {onDownloadCertificate && (
              <button
                type="button" onClick={onDownloadCertificate} disabled={downloadingCertificate}
                style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  background: "none", border: "1px solid #4b5563", color: downloadingCertificate ? "#4b5563" : "#d1d5db",
                  borderRadius: "6px", padding: "5px 12px", fontSize: "11.5px", fontWeight: 700,
                  cursor: downloadingCertificate ? "not-allowed" : "pointer",
                }}
              >
                {downloadingCertificate
                  ? <><RotateCw size={12} style={{ animation: "spin 1s linear infinite" }} /> Downloading…</>
                  : <><Download size={12} /> Download certificate</>}
              </button>
            )}
          </div>

          {!!flaggedCategories.length && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "12px" }}>
              {flaggedCategories.map(([category, severity]) => (
                <span key={category} style={{
                  fontSize: "11px", fontWeight: 700, color: severityColor(severity),
                  background: severityColor(severity) + "18", border: `1px solid ${severityColor(severity)}40`,
                  borderRadius: "10px", padding: "3px 9px",
                }}>
                  {category} · {severity}
                </span>
              ))}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {authorities.map(([board, data]) => (
              <div key={board} style={{ display: "flex", alignItems: "flex-start", gap: "10px", padding: "8px 10px", background: "#21222D", borderRadius: "7px" }}>
                <span style={{ color: "#fff", fontSize: "11.5px", fontWeight: 700, minWidth: "90px", flexShrink: 0 }}>{board}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "#F6255A", fontSize: "12px", fontWeight: 700, marginBottom: "2px" }}>{data.predicted}</div>
                  {data.criteria && <div style={{ color: "#9ca3af", fontSize: "11px", lineHeight: 1.5 }}>{data.criteria}</div>}
                  {!!(data.driven_by || []).length && (
                    <div style={{ color: "#6b7280", fontSize: "10.5px", marginTop: "3px" }}>Driven by: {data.driven_by.join(", ")}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {predictedRating.disclaimer && (
            <p style={{ color: "#6b7280", fontSize: "10.5px", lineHeight: 1.6, margin: "12px 0 0" }}>{predictedRating.disclaimer}</p>
          )}
          {certificateError && <p style={{ color: "#f97316", fontSize: "11px", margin: "8px 0 0" }}>{certificateError}</p>}
        </div>
      )}

      {audienceFit && <IarcAudienceFitBanner label="Stated audience fit" fit={audienceFit} />}
      {contentAudienceFit && <IarcAudienceFitBanner label="Generated content audience fit" fit={contentAudienceFit} />}
    </div>
  )
}

// The exact same interactive tree (QuestionBody + TopicCards) the full-screen questionnaire
// renders — shown directly inside Content Rating's own field list once there's at least one
// answer, so the user can properly see every selected choice (not a flattened summary) and
// still change any of them right there, instead of just repeating "Add Certificate" for
// something that's already been started/finished. "Next" is a placeholder for now: it currently
// just reopens the full-screen questionnaire, pending a follow-up on what it should actually do.
// Starts locked (read-only) whenever there's already at least one answer — protects against an
// accidental stray click on screen silently changing a saved choice. "Edit" unlocks the tree for
// changes; "Save" persists (via onSave, the same save used elsewhere) and locks it again.
export const IarcAnswersSummary = ({
  value, onChange, onSave, saving, insights, onDownloadCertificate, downloadingCertificate, certificateError,
}) => {
  const childrenByParent = useMemo(() => buildChildrenByParent(iarcSchema), [])
  const answers = useMemo(() => value || {}, [value])
  const rootSelected = answers[iarcSchema.root] || []
  const topics = childrenByParent[iarcSchema.root] || []
  const progress = useMemo(() => countProgress(iarcSchema.root, iarcSchema, childrenByParent, answers), [answers, childrenByParent])
  const handleAnswer = makeAnswerHandler(answers, childrenByParent, onChange)
  const [editing, setEditing] = useState(false)
  const readOnly = !editing

  const handleToggle = async () => {
    if (!editing) { setEditing(true); return }
    await onSave?.()
    setEditing(false)
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", marginBottom: "14px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#21222D", border: "1px solid #2E3244", borderRadius: "10px", padding: "8px 14px" }}>
          <div style={{ height: "5px", width: "80px", background: "#2E3244", borderRadius: "3px", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%`, background: "#F6255A", borderRadius: "3px", transition: "width 0.3s" }} />
          </div>
          <span style={{ color: "#9ca3af", fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap" }}>{progress.done}/{progress.total} answered</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <button
          type="button" onClick={handleToggle} disabled={editing && saving}
          style={{
            display: "flex", alignItems: "center", gap: "5px",
            background: editing ? "#F6255A14" : "none",
            border: `1px solid ${editing ? "#F6255A40" : "#4b5563"}`,
            color: editing ? "#F6255A" : "#d1d5db",
            borderRadius: "6px", padding: "7px 14px", fontSize: "12.5px", fontWeight: 700,
            cursor: editing && saving ? "not-allowed" : "pointer",
          }}
        >
          {editing
            ? (saving ? <><RotateCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Saving…</> : <><Save size={13} /> Save</>)
            : <><Pencil size={13} /> Edit</>}
        </button>
        </div>
      </div>

      <IarcInsightsPanel
        insights={insights} onDownloadCertificate={onDownloadCertificate}
        downloadingCertificate={downloadingCertificate} certificateError={certificateError}
      />

      <div style={{ background: "#21222D", border: "1px solid #2E3244", borderRadius: "12px", padding: "16px 18px", marginBottom: "14px" }}>
        <QuestionBody questionId={iarcSchema.root} schema={iarcSchema} childrenByParent={childrenByParent} answers={answers} onAnswer={handleAnswer} depth={0} readOnly={readOnly} />
      </div>

      {rootSelected.length > 0 && (
        <div>
          {topics.filter(id => isChildVisible(iarcSchema.questions[id], rootSelected)).map(id => (
            <TopicCard key={id} questionId={id} schema={iarcSchema} childrenByParent={childrenByParent} answers={answers} onAnswer={handleAnswer} readOnly={readOnly} />
          ))}
        </div>
      )}
    </div>
  )
}

const IarcCertificateTab = ({
  value, onChange, onBack, onGenerate, generating, generateDisabled,
  onValidate, validating, validateDisabled, validateResult,
  onSave, saving, onAutoSave,
  insights, onDownloadCertificate, downloadingCertificate, certificateError,
}) => {
  const childrenByParent = useMemo(() => buildChildrenByParent(iarcSchema), [])
  const answers = useMemo(() => value || {}, [value])
  const rootSelected = answers[iarcSchema.root] || []
  const topics = childrenByParent[iarcSchema.root] || []
  const progress = useMemo(() => countProgress(iarcSchema.root, iarcSchema, childrenByParent, answers), [answers, childrenByParent])

  const handleAnswer = makeAnswerHandler(answers, childrenByParent, onChange)

  const handleGenerate = async () => {
    if (!onGenerate) return
    const res = await onGenerate()
    if (!res) return
    const recognized = extractIarcAnswers(res, iarcSchema)
    if (!Object.keys(recognized).length) {
      console.warn("Generate (iarc_questionnaire) succeeded but no recognizable question IDs were found in the response — check the shape logged above and share it so extractIarcAnswers() can be corrected.")
      return
    }
    // Re-prune from the root so anything the AI answered without also answering its own
    // trigger question doesn't linger as an invisible, orphaned entry.
    const merged = pruneInvisible(iarcSchema.root, { ...answers, ...recognized }, iarcSchema, childrenByParent)
    onChange(merged)
    // Persist right away rather than waiting for a manual "Save answers" click — passing the
    // freshly-computed value directly since onChange's setState hasn't landed yet at this point.
    onAutoSave?.(merged)
  }

  return (
    <div style={{ flex: 1, minWidth: 0, overflowY: "auto", padding: "22px 28px 60px" }}>
      <button
        type="button" onClick={onBack}
        style={{ display: "flex", alignItems: "center", gap: "5px", background: "none", border: "none", color: "#6b7280", fontSize: "13px", cursor: "pointer", padding: 0, marginBottom: "18px" }}
      >
        <ChevronLeft size={15} /> Back to Content rating
      </button>

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px", marginBottom: "22px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
          <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: "#F6255A18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Shield size={18} color="#F6255A" />
          </div>
          <div>
            <h2 style={{ color: "#fff", fontSize: "16px", fontWeight: 700, margin: "0 0 4px" }}>IARC content rating questionnaire</h2>
            <p style={{ color: "#6b7280", fontSize: "12.5px", margin: 0, lineHeight: 1.6, maxWidth: "560px" }}>
              The same questions IARC's own certification tool asks — answering them here doesn't submit anything yet, that integration comes later.
            </p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#21222D", border: "1px solid #2E3244", borderRadius: "10px", padding: "10px 16px", flexShrink: 0 }}>
          <div style={{ height: "5px", width: "80px", background: "#2E3244", borderRadius: "3px", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%`, background: "#F6255A", borderRadius: "3px", transition: "width 0.3s" }} />
          </div>
          <span style={{ color: "#9ca3af", fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap" }}>{progress.done}/{progress.total} answered</span>
        </div>
      </div>

      {(onGenerate || onValidate || onSave) && (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "18px", flexWrap: "wrap" }}>
          {onGenerate && (
            <button
              type="button" onClick={handleGenerate} disabled={generating || generateDisabled}
              style={{
                display: "flex", alignItems: "center", gap: "7px",
                background: generating || generateDisabled ? "#2E3244" : "#F6255A", border: "none",
                color: generating || generateDisabled ? "#6b7280" : "#fff",
                borderRadius: "8px", padding: "9px 16px", fontSize: "13px", fontWeight: 700,
                cursor: generating || generateDisabled ? "not-allowed" : "pointer",
              }}
            >
              {generating
                ? <><RotateCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Generating…</>
                : <><Sparkles size={13} /> Generate with AI</>}
            </button>
          )}
          {onValidate && (
            <button
              type="button" onClick={onValidate} disabled={validating || validateDisabled}
              style={{
                display: "flex", alignItems: "center", gap: "7px",
                background: "none", border: `1px solid ${validating || validateDisabled ? "#2E3244" : "#4b5563"}`,
                color: validating || validateDisabled ? "#4b5563" : "#d1d5db",
                borderRadius: "8px", padding: "9px 16px", fontSize: "13px", fontWeight: 700,
                cursor: validating || validateDisabled ? "not-allowed" : "pointer",
              }}
            >
              {validating
                ? <><RotateCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Validating…</>
                : <><ShieldCheck size={13} /> Validate</>}
            </button>
          )}
          {validateResult && !validating && !generating && (
            <span style={{ fontSize: "12px", fontWeight: 700, color: scoreColor(validateResult.score) }}>
              {validateResult.score}/100
            </span>
          )}
          {onSave && (
            <button
              type="button" onClick={onSave} disabled={saving}
              title="Save these answers now so they aren't lost if you navigate away or reload"
              style={{
                display: "flex", alignItems: "center", gap: "7px", marginLeft: "auto",
                background: "none", border: "none", color: saving ? "#4b5563" : "#6b7280",
                fontSize: "12.5px", fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", padding: 0,
              }}
            >
              <Save size={13} /> {saving ? "Saving…" : "Save answers"}
            </button>
          )}
        </div>
      )}
      {validateResult && !validating && !generating && validateResult.feedback && (
        <p style={{ color: "#6b7280", fontSize: "11.5px", lineHeight: 1.6, margin: "-10px 0 18px", whiteSpace: "pre-line" }}>
          {validateResult.feedback}
        </p>
      )}

      <IarcInsightsPanel
        insights={insights} onDownloadCertificate={onDownloadCertificate}
        downloadingCertificate={downloadingCertificate} certificateError={certificateError}
      />

      {/* Root — which kind of app this is, gating everything else below */}
      <div style={{ background: "#21222D", border: "1px solid #2E3244", borderRadius: "12px", padding: "18px 20px", marginBottom: "18px" }}>
        <QuestionBody questionId={iarcSchema.root} schema={iarcSchema} childrenByParent={childrenByParent} answers={answers} onAnswer={handleAnswer} depth={0} />
      </div>

      {/* Topics — only appear once the root question above is answered */}
      {rootSelected.length > 0 && (
        <div>
          {topics.filter(id => isChildVisible(iarcSchema.questions[id], rootSelected)).map(id => (
            <TopicCard key={id} questionId={id} schema={iarcSchema} childrenByParent={childrenByParent} answers={answers} onAnswer={handleAnswer} />
          ))}
        </div>
      )}
    </div>
  )
}

export default IarcCertificateTab
